import { useState, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, ZoomIn, Download, Play } from "lucide-react";

/**
 * MediaGallery (also exported as ImageGallery for backwards compat)
 *
 * Props:
 *  - items: Array<{ url: string, type?: string, filename?: string }>
 *      type: mime type string e.g. "image/png", "video/mp4"
 *             if omitted, treated as image
 *  - initialIndex?: number — open lightbox at this index on mount (optional)
 *
 * Backwards-compat shorthand:
 *  - images: string[]  — plain URL array, all treated as images
 */

async function downloadFile(url, filename) {
  const response = await fetch(url, { credentials: "include" });
  const blob = await response.blob();
  const ext = blob.type.split("/")[1] || "jpg";
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename || `file.${ext}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(a.href);
}

function isVideo(type) {
  return typeof type === "string" && type.startsWith("video/");
}

export default function MediaGallery({ items, images, initialIndex = null }) {
  // Normalise: accept either `items` or legacy `images` prop
  const normalised = items
    ? items
    : (images || []).map((url) => ({ url, type: "image/*" }));

  const [lightboxIndex, setLightboxIndex] = useState(initialIndex);
  const [isDownloading, setIsDownloading] = useState(false);

  const isOpen = lightboxIndex !== null;
  const open = (index) => setLightboxIndex(index);
  const close = () => setLightboxIndex(null);

  const prev = useCallback(() => {
    setLightboxIndex((i) => (i - 1 + normalised.length) % normalised.length);
  }, [normalised.length]);

  const next = useCallback(() => {
    setLightboxIndex((i) => (i + 1) % normalised.length);
  }, [normalised.length]);

  const handleDownload = useCallback(
    async (e) => {
      e.stopPropagation();
      if (isDownloading) return;
      setIsDownloading(true);
      try {
        const item = normalised[lightboxIndex];
        await downloadFile(item.url, item.filename || `file-${lightboxIndex + 1}`);
      } catch (err) {
        console.error("Download failed:", err);
      } finally {
        setIsDownloading(false);
      }
    },
    [normalised, lightboxIndex, isDownloading]
  );

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, prev, next]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  if (!normalised.length)
    return <p className="text-sm text-muted-foreground">No media available.</p>;

  const activeItem = isOpen ? normalised[lightboxIndex] : null;

  return (
    <>
      {/* Thumbnail grid */}
      <div className="flex gap-3 flex-wrap">
        {normalised.map((item, index) => (
          <button
            key={index}
            onClick={() => open(index)}
            className="group relative w-24 h-24 min-w-[6rem] border rounded overflow-hidden flex-shrink-0 bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={`Preview ${isVideo(item.type) ? "video" : "image"} ${index + 1}`}
          >
            {isVideo(item.type) ? (
              <>
                <video
                  src={item.url}
                  className="w-full h-full object-cover"
                  preload="metadata"
                  muted
                />
                {/* Play badge */}
                <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/50 transition-colors duration-200">
                  <div className="rounded-full bg-white/80 p-1.5">
                    <Play className="w-4 h-4 text-black fill-black" />
                  </div>
                </div>
              </>
            ) : (
              <>
                <img
                  src={item.url}
                  alt={item.filename || `Media ${index + 1}`}
                  className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://via.placeholder.com/96x96?text=Error";
                  }}
                />
                {/* Zoom overlay */}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                  <ZoomIn className="w-5 h-5 text-white" />
                </div>
              </>
            )}
          </button>
        ))}
      </div>

      {/* Lightbox */}
      {isOpen && activeItem && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4"
          onClick={close}
          role="dialog"
          aria-modal="true"
          aria-label="Media preview"
        >
          <div
            className="relative flex flex-col items-center max-w-5xl w-full max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Top bar */}
            <div className="absolute -top-10 left-0 right-0 flex items-center justify-between">
              <span className="text-white text-sm select-none">
                {lightboxIndex + 1} / {normalised.length}
              </span>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleDownload}
                  disabled={isDownloading}
                  className="text-white hover:text-gray-300 transition-colors disabled:opacity-50"
                  aria-label="Download"
                  title="Download"
                >
                  <Download className="w-5 h-5" />
                </button>
                <button
                  onClick={close}
                  className="text-white hover:text-gray-300 transition-colors"
                  aria-label="Close preview"
                >
                  <X className="w-7 h-7" />
                </button>
              </div>
            </div>

            {/* Main media */}
            {isVideo(activeItem.type) ? (
              <video
                key={activeItem.url}
                src={activeItem.url}
                controls
                autoPlay
                className="max-h-[80vh] max-w-full rounded shadow-2xl object-contain bg-black"
              />
            ) : (
              <img
                src={activeItem.url}
                alt={activeItem.filename || `Media ${lightboxIndex + 1}`}
                className="max-h-[80vh] max-w-full rounded shadow-2xl object-contain"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://via.placeholder.com/800x600?text=Error";
                }}
              />
            )}

            {/* Prev / Next */}
            {normalised.length > 1 && (
              <>
                <button
                  onClick={prev}
                  className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-12 text-white hover:text-gray-300 transition-colors"
                  aria-label="Previous"
                >
                  <ChevronLeft className="w-9 h-9" />
                </button>
                <button
                  onClick={next}
                  className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-12 text-white hover:text-gray-300 transition-colors"
                  aria-label="Next"
                >
                  <ChevronRight className="w-9 h-9" />
                </button>
              </>
            )}

            {/* Thumbnail strip */}
            {normalised.length > 1 && (
              <div className="flex gap-2 mt-4 overflow-x-auto max-w-full pb-1">
                {normalised.map((item, index) => (
                  <button
                    key={index}
                    onClick={() => setLightboxIndex(index)}
                    className={`relative w-14 h-14 min-w-[3.5rem] rounded overflow-hidden border-2 transition-all flex-shrink-0 bg-muted ${
                      index === lightboxIndex
                        ? "border-white opacity-100"
                        : "border-transparent opacity-50 hover:opacity-80"
                    }`}
                    aria-label={`Go to item ${index + 1}`}
                  >
                    {isVideo(item.type) ? (
                      <>
                        <video src={item.url} className="w-full h-full object-cover" preload="metadata" muted />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <Play className="w-3 h-3 text-white fill-white" />
                        </div>
                      </>
                    ) : (
                      <img
                        src={item.url}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// Backwards-compat alias
export { MediaGallery as ImageGallery };
